import "@/App.css";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { applyStoredAccessibility } from "@/lib/accessibility";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { EntityPreviewProvider } from "@/contexts/EntityPreviewContext";
import { StatsProvider } from "@/contexts/StatsContext";
import EntityPreviewModal from "@/components/EntityPreviewModal";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import Layout from "@/components/Layout";
import DashboardPage from "@/pages/DashboardPage";
import UserManagementPage from "@/pages/UserManagementPage";
import CompanyRegistryPage from "@/pages/CompanyRegistryPage";
import InstrumentTypesPage from "@/pages/InstrumentTypesPage";
import InstrumentRegistryPage from "@/pages/InstrumentRegistryPage";
import InstrumentDetailPage from "@/pages/InstrumentDetailPage";
import EtalonPage from "@/pages/EtalonPage";
import MethodologyPage from "@/pages/MethodologyPage";
import CalibrationPage from "@/pages/CalibrationPage";
import CalibrationDetailPage from "@/pages/CalibrationDetailPage";
import CalibrationRunScreen from "@/pages/CalibrationRunScreen";
import ApprovalPage from "@/pages/ApprovalPage";
import EnvironmentalPage from "@/pages/EnvironmentalPage";
import OrderRegistryPage from "@/pages/OrderRegistryPage";
import OrderDetailPage from "@/pages/OrderDetailPage";
import IntakeRegistryPage from "@/pages/IntakeRegistryPage";
import IntakeDetailPage from "@/pages/IntakeDetailPage";
import DispatchPage from "@/pages/DispatchPage";
import CertificateRegistryPage from "@/pages/CertificateRegistryPage";
import LabSettingsPage from "@/pages/LabSettingsPage";
import LaboratoriesPage from "@/pages/LaboratoriesPage";
import MyAccountPage from "@/pages/MyAccountPage";
import ChatPage from "@/pages/ChatPage";
import RegisterLabPage from "@/pages/RegisterLabPage";
import SuperAdminPage from "@/pages/SuperAdminPage";
import ChatBubble from "@/components/ChatBubble";

function ProtectedRoute({ children, adminOnly = false, fullscreen = false }) {
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--ct-bg)' }}>
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto mb-3" style={{ borderColor: 'var(--ct-accent)', borderTopColor: 'transparent' }} />
          <p className="text-sm" style={{ color: 'var(--ct-text-secondary)' }}>{t('common.loading')}</p>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && !user.isAdmin) return <Navigate to="/" replace />;
  if (fullscreen) return children;
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--ct-bg)' }}>
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--ct-accent)', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      {/* Veřejné (multi-tenancy): registrace laboratoře (schvaluje super-admin) */}
      <Route path="/register-lab" element={<RegisterLabPage />} />
      {/* Control-plane: super-admin konzole (vlastní autentizace) */}
      <Route path="/superadmin" element={<SuperAdminPage />} />
      <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/instruments" element={<ProtectedRoute><InstrumentRegistryPage /></ProtectedRoute>} />
      <Route path="/instruments/:id" element={<ProtectedRoute><InstrumentDetailPage /></ProtectedRoute>} />
      <Route path="/orders" element={<ProtectedRoute><OrderRegistryPage /></ProtectedRoute>} />
      <Route path="/orders/:id" element={<ProtectedRoute><OrderDetailPage /></ProtectedRoute>} />
      <Route path="/intakes" element={<ProtectedRoute><IntakeRegistryPage /></ProtectedRoute>} />
      <Route path="/intakes/:id" element={<ProtectedRoute><IntakeDetailPage /></ProtectedRoute>} />
      <Route path="/calibrations" element={<ProtectedRoute><CalibrationPage /></ProtectedRoute>} />
      <Route path="/calibrations/:id" element={<ProtectedRoute><CalibrationDetailPage /></ProtectedRoute>} />
      <Route path="/calibrations/:id/run" element={<ProtectedRoute fullscreen><CalibrationRunScreen /></ProtectedRoute>} />
      <Route path="/approvals" element={<ProtectedRoute><ApprovalPage /></ProtectedRoute>} />
      <Route path="/dispatches" element={<ProtectedRoute><DispatchPage /></ProtectedRoute>} />
      <Route path="/certificates" element={<ProtectedRoute><CertificateRegistryPage /></ProtectedRoute>} />
      <Route path="/companies" element={<ProtectedRoute><CompanyRegistryPage /></ProtectedRoute>} />
      <Route path="/instrument-types" element={<ProtectedRoute><InstrumentTypesPage /></ProtectedRoute>} />
      <Route path="/etalons" element={<ProtectedRoute><EtalonPage /></ProtectedRoute>} />
      <Route path="/environment" element={<ProtectedRoute><EnvironmentalPage /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute adminOnly><UserManagementPage /></ProtectedRoute>} />
      <Route path="/methodology" element={<ProtectedRoute adminOnly><MethodologyPage /></ProtectedRoute>} />
      <Route path="/laboratories" element={<ProtectedRoute adminOnly><LaboratoriesPage /></ProtectedRoute>} />
      <Route path="/lab-settings" element={<ProtectedRoute adminOnly><LabSettingsPage /></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
      <Route path="/my-account" element={<ProtectedRoute><MyAccountPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    applyStoredAccessibility();
  }, []);
  return (
    <BrowserRouter
      basename={process.env.REACT_APP_BASENAME || process.env.PUBLIC_URL || ""}
    >
      <ThemeProvider>
      <AuthProvider>
        <StatsProvider>
        <EntityPreviewProvider>
          <AppRoutes />
          <EntityPreviewModal />
          <Toaster
            position="bottom-right"
            toastOptions={{
              style: { background: 'var(--ct-surface)', border: '1px solid var(--ct-border)', color: 'var(--ct-text)' },
            }}
          />
        </EntityPreviewProvider>
        </StatsProvider>
      </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
